import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import {

  useGetEJournal,
} from "../misc/mutations";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";

const formSchema = z.object({
  terminalId: z.string().optional(),
  stan: z.string().optional(),
  // rrn: z.string().optional(),
});

const EJournal = () => {
  const [transactionReport, setTransactionReport] = useState<any>(null);
  const reportRef = useRef<any>(null);
  const form = useForm({
    resolver: zodResolver(formSchema),
  });
  const {
    data: terminal,
    isLoading: terminalIsLoading,
    refetch: refetchTerminal,
  } = useGetEJournal({
    terminalId: form.watch("terminalId"),
    stan: form.watch("stan"),
  });

  useEffect(() => {
    if (!terminal) return;
    setTransactionReport(terminal.transaction);
  }, [terminal]);

  const onSubmit = (data: any) => {
    refetchTerminal(data);
  };
  const handlePrint = useReactToPrint({
    documentTitle: `Ticket-${transactionReport?.id || "download"}`,
    onAfterPrint: () => toast.success("PDF downloaded successfully"),
    contentRef: reportRef,
  });

  useEffect(() => {
    if (!transactionReport) return;
    handleDownload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionReport]);

  const handleDownload = () => {
    handlePrint();
  };

  return (
    <div>
      <div style={{ display: "none" }}>
        <TransactionReceipt
          ref={reportRef}
          transactionReport={transactionReport}
        />
      </div>
      <h1 className="text-2xl">E-Journal</h1>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="mt-8 gap-4 flex items-center"
        >
          <FormField
            control={form.control}
            name="terminalId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Terminal ID</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="stan"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Stan</FormLabel>
                <FormControl>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            className="self-end place-items-end justify-self-end bg-primary-500 disabled:bg-primary-100"
            // onClick={handleDownloadFile}
            type="submit"
            disabled={terminalIsLoading}
          >
            {terminalIsLoading ? (
              <Loader2 className="animate-spin" />
            ) : (
              "Submit"
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default EJournal;

const TransactionReceipt = React.forwardRef<
  HTMLDivElement,
  { transactionReport: any }
>(({ transactionReport }, ref) => {
  if (!transactionReport) return null;

  const {
    amount,
    cardHolder,
    cardType,
    currency,
    date,
    merchantName,
    rrn,
    stan,
    status,
    terminalId,
    transactionId,
    logo,
  } = transactionReport;

  return (
    <div ref={ref} className="max-w-xl mx-auto bg-white p-6 font-sans">
      {/* Merchant Name and Logo */}
      <div className="flex justify-center items-center gap-4 mb-4">
        <div className="w-12 h-12 flex items-center justify-center rounded">
          <img
            src={logo}
            alt="Merchant Logo"
            className="max-w-full max-h-full"
          />
        </div>
        <div className="">
          <div className="text-xl font-bold">{merchantName}</div>
        </div>
      </div>

      {/* Receipt Content */}
      <div className="mt-6">
        {/* Date Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">Date:</div>
          <div className="w-2/3 py-3 px-4 bg-gray-100">
            {new Date(date).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </div>
        </div>

        {/* Transaction ID Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">Transaction ID:</div>
          <div className="w-2/3 py-3 px-4">{transactionId}</div>
        </div>

        {/* Amount Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">Amount:</div>
          <div className="w-2/3 py-3 px-4 bg-gray-100">
            {currency} {amount.toLocaleLowerCase()}
          </div>
        </div>

        {/* Cardholder Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">Cardholder:</div>
          <div className="w-2/3 py-3 px-4">{cardHolder}</div>
        </div>

        {/* Card Type Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">Card Type:</div>
          <div className="w-2/3 py-3 px-4 bg-gray-100">{cardType}</div>
        </div>

        {/* RRN Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">RRN:</div>
          <div className="w-2/3 py-3 px-4 bg-gray-100">{rrn}</div>
        </div>

        {/* STAN Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">STAN:</div>
          <div className="w-2/3 py-3 px-4">{stan}</div>
        </div>

        {/* Status Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">Status:</div>
          <div className="w-2/3 py-3 px-4">{status.toUpperCase()}</div>
        </div>

        {/* Terminal ID Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">Terminal ID:</div>
          <div className="w-2/3 py-3 px-4 bg-gray-100">{terminalId}</div>
        </div>
      </div>
    </div>
  );
});
